import { useRef, useState } from "react";
import axios from "axios";
import Papa from "papaparse";
import { toast } from "sonner";
import { Upload, FileText, X, Check, AlertCircle, Loader2, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { API } from "@/App";

const TEMPLATE_HEADERS = ["name", "description", "price", "category", "image_url", "inventory", "is_active"];
const TEMPLATE_ROW = [
  "Heritage Hot Sauce",
  "Small-batch hot sauce, 5oz bottle.",
  "14.99",
  "Pantry",
  "https://example.com/hot-sauce.jpg",
  "25",
  "true",
];

function downloadTemplate() {
  const csv = [
    TEMPLATE_HEADERS.join(","),
    TEMPLATE_ROW.map((v) => (v.includes(",") ? `"${v}"` : v)).join(","),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "product-import-template.csv";
  a.click();
  URL.revokeObjectURL(url);
}

function coerceRow(raw) {
  // Normalize header casing/spacing
  const r = {};
  Object.entries(raw).forEach(([k, v]) => {
    r[k.trim().toLowerCase().replace(/\s+/g, "_")] = typeof v === "string" ? v.trim() : v;
  });

  if (!r.name) return { error: "Missing name" };

  const priceNum = parseFloat((r.price || "").toString().replace(/[$,]/g, ""));
  if (Number.isNaN(priceNum) || priceNum < 0) return { error: "Invalid price" };

  const inventory = r.inventory ? parseInt(r.inventory, 10) : 0;
  const isActive = (r.is_active ?? "true").toString().toLowerCase();

  return {
    product: {
      name: r.name,
      description: r.description || `${r.name} from a Black-owned business in The Blk Community.`,
      price: priceNum,
      category: r.category || null,
      image_url: r.image_url || null,
      inventory: Number.isNaN(inventory) ? 0 : inventory,
      is_active: !["false", "0", "no", "off"].includes(isActive),
    },
  };
}

export default function BulkProductUpload({
  businessId,
  businessName,
  triggerLabel = "Bulk Upload",
  buttonClassName,
  onCreated,
}) {
  const fileRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [rawCsv, setRawCsv] = useState("");
  const [parsed, setParsed] = useState([]); // [{product, error?}]
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setRawCsv("");
    setParsed([]);
    setResult(null);
  };

  const parseCsv = (text) => {
    setRawCsv(text);
    setResult(null);
    if (!text.trim()) {
      setParsed([]);
      return;
    }
    const out = Papa.parse(text, { header: true, skipEmptyLines: true });
    const rows = (out.data || []).map(coerceRow);
    setParsed(rows);
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => parseCsv(ev.target.result || "");
    reader.readAsText(file);
  };

  const validRows = parsed.filter((r) => !r.error);
  const invalidRows = parsed.filter((r) => r.error);

  const submit = async () => {
    if (validRows.length === 0) {
      toast.error("No valid products to import");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/businesses/${businessId}/products/bulk`,
        { products: validRows.map((r) => r.product) },
        { withCredentials: true }
      );
      setResult({ ok: true, count: res.data?.created || 0 });
      toast.success(`${res.data?.created || 0} product(s) added to ${businessName || "this business"}`);
      if (onCreated) onCreated(res.data?.products || []);
    } catch (err) {
      const msg = err.response?.data?.detail || "Import failed";
      setResult({ ok: false, error: msg });
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) setTimeout(reset, 200);
      }}
    >
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className={buttonClassName}
          data-testid="bulk-products-trigger"
        >
          <Upload className="w-4 h-4 mr-1" />
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="bulk-products-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-tbc-navy">
            Bulk Product Upload{businessName ? ` — ${businessName}` : ""}
          </DialogTitle>
          <p className="text-sm text-gray-500">
            Upload a CSV with up to 200 products at once. Required columns:{" "}
            <code className="bg-gray-100 px-1 rounded">name</code>,{" "}
            <code className="bg-gray-100 px-1 rounded">price</code>. Optional:{" "}
            <code className="bg-gray-100 px-1 rounded">description</code>,{" "}
            <code className="bg-gray-100 px-1 rounded">category</code>,{" "}
            <code className="bg-gray-100 px-1 rounded">image_url</code>,{" "}
            <code className="bg-gray-100 px-1 rounded">inventory</code>,{" "}
            <code className="bg-gray-100 px-1 rounded">is_active</code>.
          </p>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="flex flex-wrap gap-2 items-center">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={downloadTemplate}
              className="text-tbc-navy"
              data-testid="bulk-download-template"
            >
              <Download className="w-4 h-4 mr-1" /> Download template
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => fileRef.current?.click()}
              data-testid="bulk-choose-file"
            >
              <FileText className="w-4 h-4 mr-1" /> Choose CSV file
            </Button>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              onChange={handleFile}
              className="hidden"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">
              Or paste CSV below
            </label>
            <Textarea
              value={rawCsv}
              onChange={(e) => parseCsv(e.target.value)}
              placeholder={`name,price,description,inventory\nHeritage Hot Sauce,14.99,Small-batch hot sauce,25`}
              rows={8}
              className="font-mono text-xs"
              data-testid="bulk-csv-textarea"
            />
          </div>

          {parsed.length > 0 && (
            <div className="border rounded-lg overflow-hidden" data-testid="bulk-preview">
              <div className="px-4 py-2 bg-gray-50 flex items-center justify-between text-sm border-b">
                <span className="text-gray-600">
                  <strong className="text-tbc-navy">{validRows.length}</strong> ready to import
                  {invalidRows.length > 0 && (
                    <span className="text-red-600 ml-2">· {invalidRows.length} invalid</span>
                  )}
                </span>
              </div>
              <div className="max-h-64 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-gray-500 uppercase text-[10px] tracking-wider">
                    <tr>
                      <th className="px-3 py-2 text-left">Status</th>
                      <th className="px-3 py-2 text-left">Name</th>
                      <th className="px-3 py-2 text-right">Price</th>
                      <th className="px-3 py-2 text-right">Inv.</th>
                      <th className="px-3 py-2 text-left">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.slice(0, 50).map((r, idx) => (
                      <tr key={`bulk-row-${r.product?.sku || r.product?.name || idx}`} className={r.error ? "bg-red-50" : ""}>
                        <td className="px-3 py-1.5">
                          {r.error ? (
                            <AlertCircle className="w-3.5 h-3.5 text-red-500" />
                          ) : (
                            <Check className="w-3.5 h-3.5 text-green-600" />
                          )}
                        </td>
                        <td className="px-3 py-1.5 truncate max-w-xs">
                          {r.product?.name || <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-3 py-1.5 text-right">
                          {r.product ? `$${r.product.price.toFixed(2)}` : "—"}
                        </td>
                        <td className="px-3 py-1.5 text-right">
                          {r.product?.inventory ?? "—"}
                        </td>
                        <td className="px-3 py-1.5 text-red-600">{r.error || ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {parsed.length > 50 && (
                  <p className="text-xs text-gray-400 px-3 py-2 border-t">
                    Showing 50 of {parsed.length} rows.
                  </p>
                )}
              </div>
            </div>
          )}

          {result && (
            <div
              className={`rounded-lg p-3 text-sm ${
                result.ok
                  ? "bg-green-50 text-green-700 border border-green-200"
                  : "bg-red-50 text-red-700 border border-red-200"
              }`}
              data-testid="bulk-result"
            >
              {result.ok ? (
                <span>
                  <Check className="w-4 h-4 inline mr-1" /> {result.count} product(s) created successfully.
                </span>
              ) : (
                <span>
                  <X className="w-4 h-4 inline mr-1" /> {result.error}
                </span>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={submitting}
          >
            Close
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || validRows.length === 0}
            className="bg-tbc-navy hover:bg-tbc-navy/90 text-white"
            data-testid="bulk-import-submit"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing…
              </>
            ) : (
              `Import ${validRows.length || ""} product(s)`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
